import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

import SalesModal from '../components/Modal/SalesModal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { fetchUsersByStatus } from '../utils/userApi';

const SALES_API = 'https://system.sales-management.cloud/api/sales.php';

function Sales() {
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'exclude_cancelled'
  });

  // 売上一覧を取得
  const fetchSales = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      let url = SALES_API;
      const params = new URLSearchParams();
      
      if (filters.startDate) {
        params.append('start_date', filters.startDate);
      }
      if (filters.endDate) {
        params.append('end_date', filters.endDate);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      params.append('limit', '100');

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSales(data.data.sales || []);
      } else {
        setError(data.message || '売上の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // ユーザー一覧を取得
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsersByStatus('active', 100);
        setUsers(userList);
      } catch (err) {
        console.error('ユーザー取得エラー:', err);
      }
    };
    loadUsers();
  }, []);

  // ユーザーIDから名前を取得
  const getUserName = (userId) => {
    if (!userId) return '-';
    const user = users.find(u => u.id === userId);
    return user ? user.name : `ID: ${userId}`;
  };

  // 売上の登録・更新
  const handleSubmit = async (formData) => {
    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const isUpdate = !!selectedSale;
      const requestBody = {
        sale_date: formData.saleDate,
        final_amount: parseInt(formData.finalAmount),
        client_name: formData.clientName,
        status: formData.status
      };

      // 更新時はsale_idを追加
      if (isUpdate) {
        requestBody.sale_id = selectedSale.sale_id;
      }

      if (formData.productName) requestBody.product_name = formData.productName;
      if (formData.expenseAmount) requestBody.expense_amount = parseInt(formData.expenseAmount);
      if (formData.paymentMethod) requestBody.payment_method = formData.paymentMethod;
      if (formData.paymentDate) requestBody.payment_date = formData.paymentDate;
      if (formData.constructionDate) requestBody.construction_date = formData.constructionDate;
      if (formData.pointerUserId) requestBody.pointer_user_id = parseInt(formData.pointerUserId);
      if (formData.closerUserId) requestBody.closer_user_id = parseInt(formData.closerUserId);
      if (formData.notes) requestBody.notes = formData.notes;

      const response = await fetch(SALES_API, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess(isUpdate ? '売上を更新しました' : '売上を登録しました');
        setIsModalOpen(false);
        setSelectedSale(null);
        fetchSales();
      } else {
        setError(data.message || (isUpdate ? '売上の更新に失敗しました' : '売上の登録に失敗しました'));
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = (sale) => {
    setSelectedSale(sale);
    setIsModalOpen(true);
  };

  const handleNewSale = () => {
    setSelectedSale(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSale(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '保留中', className: 'bg-yellow-100 text-yellow-800' },
      completed: { label: '完了', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
      refunded: { label: '返金済み', className: 'bg-gray-100 text-gray-800' },
      deleted: { label: '削除済み', className: 'bg-gray-200 text-gray-600' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">売上管理</h1>
        <button
          onClick={handleNewSale}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <AddIcon />
          新規登録
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ステータス</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全て</option>
              <option value="exclude_cancelled">キャンセル除く</option>
              <option value="exclude_deleted">削除済み除く</option>
              <option value="pending">保留中</option>
              <option value="completed">完了</option>
              <option value="cancelled">キャンセル</option>
              <option value="refunded">返金済み</option>
              <option value="deleted">削除済み</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ startDate: '', endDate: '', status: 'exclude_cancelled' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              クリア
            </button>
          </div>
        </div>
      </div>

      {/* 売上一覧 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : sales.length === 0 ? (
          <div className="p-8 text-center text-gray-500">売上データがありません</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">売上日</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">顧客名</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品名</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金額</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">アポインター</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">クローザー</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sales.map((sale) => (
                  <tr key={sale.sale_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sale.sale_date}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {sale.client_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {sale.product_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(sale.final_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getUserName(sale.pointer_user_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getUserName(sale.closer_user_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(sale.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(sale)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <EditIcon fontSize="small" />
                        編集
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SalesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        sale={selectedSale}
        onSubmit={handleSubmit}
        loading={modalLoading}
      />
    </div>
  );
}

export default Sales;